import styled from '@emotion/styled'

import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { calcRem } from 'component-library/__styling/style-functions'
import { metrics } from 'component-library/__styling/settings'
import {
    themedBreakpoint,
    themedBreakpointMax,
} from 'component-library/__styling/themed-breakpoint'
import { themedValue } from 'component-library/__styling/themed-value'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'

export interface GridProps {
    verticalSpacing: keyof ThemeMargins | undefined
}

export const StyledMilo = styled('div')<GridProps>(
    {
        margin: '0 auto',
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        position: 'relative',
    },
    props => ({
        marginBottom: props.verticalSpacing && props.theme.margins[props.verticalSpacing],
    })
)

export const StyledHeader = styled('div')({
    display: 'block',
    marginBottom: calcRem(metrics.perthnow.margins.md),
})

export const StyledGridRow = styled('div')({
    display: 'flex',
    flex: '1 1 auto',
    width: '100%',
    textAlign: 'left',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
})

export interface ColumnProps {
    numberOfItems?: number
    removeMargin?: boolean
    largeCardPosition?: 'left' | 'right' | undefined
    preserveOrder?: boolean
}

const { sm, md } = metrics.perthnow.margins

export const StyledColumn = styled('div')<ColumnProps>(
    {
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexBasis: '100%',
        flex: '0 0 auto',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
    },
    ({ theme, numberOfItems, largeCardPosition, preserveOrder }) => ({
        [':nth-child(1)']: {
            margin: themedValue(theme, {
                sevennews: `0 0 ${calcRem(theme.margins.lg)} 0`,
                fallback: `0 0 ${calcRem(theme.margins.sm)} 0`,
            }),

            [themedBreakpoint(theme, { sevennews: 'md', fallback: 'sm' })]: {
                width: `calc(50% - ${calcRem(sm)} - 1px)`,
                flexBasis: `calc(50% - ${calcRem(sm)} - 1px)`,
                margin: `
                0 calc(.5 * ${calcRem(md)})
                calc(.5 * ${calcRem(sm)}) 0
            `,
            },
        },

        [':nth-child(2)']: {
            [themedBreakpoint(theme, { sevennews: 'md', fallback: 'sm' })]: {
                width: `calc(50% - ${calcRem(sm)} - 1px)`,
                flexBasis: `calc(50% - ${calcRem(sm)} - 1px)`,
                margin: `
            0 0
            calc(.5 * ${calcRem(sm)}) calc(.5 * ${calcRem(md)})
        `,
            },
        },

        // Remove the bottom margin if there aren't enough items to create an additional row
        marginBottom: numberOfItems && numberOfItems <= 3 ? 0 : undefined,

        ...conditionalStyles(largeCardPosition === 'right', {
            [':nth-child(1)']: {
                [themedBreakpoint(theme, { sevennews: 'md', fallback: 'sm' })]: {
                    width: `calc(50% - ${calcRem(sm)} - 1px)`,
                    flexBasis: `calc(50% - ${calcRem(sm)} - 1px)`,

                    margin: `
                0 calc(.5 * ${calcRem(md)})
                calc(.5 * ${calcRem(sm)}) 0
                `,
                },

                [themedBreakpointMax(theme, { sevennews: 'md', fallback: 'sm' })]: {
                    order: preserveOrder ? 1 : 2,
                },
            },

            [':nth-child(2)']: {
                [themedBreakpoint(theme, { sevennews: 'md', fallback: 'sm' })]: {
                    width: `calc(50% - ${calcRem(sm)} - 1px)`,
                    flexBasis: `calc(50% - ${calcRem(sm)} - 1px)`,
                    margin: `
                    0 0
                    calc(.5 * ${calcRem(sm)}) ${calcRem(sm)}
                    `,
                },

                [themedBreakpointMax(theme, { sevennews: 'md', fallback: 'sm' })]: {
                    order: preserveOrder ? 2 : 1,
                    marginBottom: calcRem(sm),
                },
            },
        }),
    })
)
export interface NestedColumnProps {
    numberOfItems?: number
    largeCardPosition?: 'left' | 'right' | undefined
}

export const StyledNestedColumn = styled('div')<NestedColumnProps>(({ theme }) => ({
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexBasis: '100%',
    flex: '0 0 auto',
    flexWrap: 'nowrap',
    marginBottom: calcRem(metrics.perthnow.margins.sm),
    alignItems: 'stretch',

    [themedBreakpoint(theme, { sevennews: 'md', fallback: 'sm' })]: {
        [':last-child']: {
            marginBottom: 0,
        },
    },
}))

export interface ColumnRepeatProps {
    lastRowOfCards: number
}

export const StyledColumnRepeat = styled('div')<ColumnRepeatProps>(
    {
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexBasis: '100%',
        flex: '0 0 auto',
        flexWrap: 'nowrap',
        margin: `calc(.5 * ${calcRem(metrics.perthnow.margins.sm)}) 0`,
        alignItems: 'stretch',

        [':last-child']: { marginBottom: 0 },
    },
    ({ lastRowOfCards, theme }) => ({
        [themedBreakpoint(theme, { sevennews: 'md', fallback: 'sm' })]: {
            width: `calc(50% - ${calcRem(metrics.perthnow.margins.sm)} - 1px)`,
            flexBasis: 'auto',

            [':nth-child(odd)']: {
                marginRight: calcRem(metrics.perthnow.margins.sm),
            },

            [':nth-child(even)']: {
                marginLeft: calcRem(metrics.perthnow.margins.sm),
            },

            [':nth-last-child(-n+' + lastRowOfCards + ')']: {
                marginBottom: 0,
            },
        },
    })
)
